import { useTranslation } from "react-i18next";
import { AlertTriangle, Info } from "lucide-react";
import { isSeriousCheck, type ApplicationCheck } from "@/lib/applicationChecks";
import { shortIsoDate } from "@/components/applicators/applicatorText";
import { cn } from "@/lib/utils";

/**
 * LOS AVISOS DE CARNÉ (H6) Y MÁQUINA (H7) DE UNA APLICACIÓN (F4 del plan del
 * cuaderno, 26-09-2026). En crear tarea (con el aplicador y la máquina que
 * saldrán en el parte) y en el paso Revisar del registro manual.
 *
 * NO BLOQUEA (T4): no deshabilita nada ni corta un guardado. Lo serio
 * (caducado, vencido, sin nº) va en ámbar; lo que avisa con tiempo («caduca en
 * 45 días») y lo que no se sabe («sin dato», A15) va en gris. Sin avisos no
 * pinta nada: no hay un «todo en regla» que nadie haya comprobado.
 */
export function ApplicationChecksNote({
  checks,
  className,
}: {
  checks: readonly ApplicationCheck[];
  className?: string;
}) {
  const { t } = useTranslation();
  if (checks.length === 0) return null;

  const who = (w: string | null) => w ?? t("applicationContext.checks.whoFallback", "el aplicador");
  const line = (c: ApplicationCheck): string => {
    switch (c.kind) {
      case "license_expired":
        return t("applicationContext.checks.licenseExpired", "Carné de {{who}} caducado el {{date}}", {
          who: who(c.who),
          date: shortIsoDate(c.expiresOn),
        });
      case "license_soon":
        return t("applicationContext.checks.licenseSoon", "Carné de {{who}}: caduca el {{date}} (en {{days}} días)", {
          who: who(c.who),
          date: shortIsoDate(c.expiresOn),
          days: c.daysLeft,
        });
      case "license_no_data":
        return t("applicationContext.checks.licenseNoData", "Carné de {{who}}: sin dato (no está registrado)", {
          who: who(c.who),
        });
      case "equipment_no_roma":
        return t("applicationContext.checks.equipmentNoRoma", "{{label}}: sin nº ROMA", { label: c.label });
      case "equipment_no_census":
        return t("applicationContext.checks.equipmentNoCensus", "{{label}}: sin nº de censo", { label: c.label });
      case "inspection_overdue":
        return t("applicationContext.checks.inspectionOverdue", "{{label}}: inspección vencida el {{date}}", {
          label: c.label,
          date: shortIsoDate(c.nextOn),
        });
      case "inspection_soon":
        return t("applicationContext.checks.inspectionSoon", "{{label}}: inspección antes del {{date}} (en {{days}} días)", {
          label: c.label,
          date: shortIsoDate(c.nextOn),
          days: c.daysLeft,
        });
      case "inspection_no_data":
        return t("applicationContext.checks.inspectionNoData", "{{label}}: inspección sin dato (sin fecha de compra ni de inspección)", {
          label: c.label,
        });
    }
  };

  return (
    <ul className={cn("space-y-1", className)} data-testid="application-checks">
      {checks.map((c, i) => {
        const serious = isSeriousCheck(c);
        const Icon = serious ? AlertTriangle : Info;
        return (
          <li
            key={`${c.kind}-${i}`}
            data-kind={c.kind}
            className={cn(
              "flex items-start gap-1.5 text-xs leading-snug",
              serious ? "font-semibold text-amber-700" : "text-muted-foreground",
            )}
          >
            <Icon className="mt-[1px] h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="min-w-0 break-words">{line(c)}</span>
          </li>
        );
      })}
    </ul>
  );
}

export default ApplicationChecksNote;
